import {
  createContext,
  useMemo,
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

import {
  getBaseURL,
  getCurrentUser,
  getJobNumbers,
  getScopes,
  invalidateScopesCache,
  getLatestAnnouncement,
  updatePreferredLanguage as persistPreferredLanguage,
} from "../../services/api/core";
import { LANGUAGE_CONFIG } from "../../config";

import {
  extractAppNameFromPath,
  extractDomainFromPath,
} from "../../utils/common/url";
import { sortAlphabetically, upsertAlphabetically } from "../../utils/collections/sort";

/**
 * All context which is useful across all apps is stored here. This is limited to basic
 * data which is not likely to be changed or updated. Such as lists of current model
 * data from the backend, user data, and basic constants like base URL.
 *
 * @context CoreContext
 *
 * @typedef {Object} CoreContextType
 * @property {Object} appName - The current app name extracted from the URL (stored as ref).
 * @property {Object} appDomain - The current app domain extracted from the URL (stored as ref).
 * @property {string|null} baseURL - The base URL of the application.
 * @property {boolean} debug - Whether the backend Django DEBUG setting is enabled.
 * @property {Object|null} user - The current user object.
 * @property {Object} impersonation - Current impersonation metadata.
 * @property {boolean} userLoading - Whether the user data is currently loading.
 * @property {Function} refreshUser - Function to refresh the current user data.
 * @property {Function} updatePreferredLanguage - Optimistically save the user's language.
 * @property {Object|null} userError - Any error that occurred while fetching user data.
 * @property {Object|null} announcement - Latest unseen announcement, if any.
 * @property {Function} fetchAnnouncement - Load announcement without clearing an open one.
 * @property {Function} clearAnnouncement - Dismiss the announcement modal locally.
 * @property {Array} jobsList - List of active job numbers only in the DB.
 * @property {Array} allJobsList - List of all job numbers (including inactive) in the DB.
 * @property {Array} scopesList - List of all active scopes of work in the DB.
 * @property {Function} addScope - Insert a newly created work scope into `scopesList`.
 * @property {Function} syncCatalogScope - Upsert an active scope, or remove one that was deactivated.
 */
export const CoreContext = createContext(null);

/**
 * Read CoreContext. Throws if used outside the authenticated Core provider.
 *
 * @returns {import("react").ContextType<typeof CoreContext>}
 */
export function useCoreContext() {
  const context = useContext(CoreContext);
  if (!context) {
    throw new Error("useCoreContext must be used within a CoreProvider");
  }
  return context;
}

/**
 * Build the CoreContext value: current user, jobs, scopes, announcement, base URL, and impersonation.
 *
 * @returns {Object} Value passed to `CoreContext.Provider`.
 */
export function useCoreCtx() {
  // App name state - extracted from URL path
  const appName = useRef(null);
  const appDomain = useRef(null);

  // Extract app name from URL path on initial load only. This will normally be set by
  // the app router, but this is a fallback (usually on page refresh).
  useEffect(() => {
    if (appName.current === null) {
      const extractedAppName = extractAppNameFromPath(window.location.pathname);
      if (extractedAppName) {
        appName.current = extractedAppName;
      }
    }
    if (appDomain.current === null) {
      const extractedDomain = extractDomainFromPath(window.location.pathname);
      if (extractedDomain) {
        appDomain.current = extractedDomain;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - runs only once on mount as fallback

  // Base URL data
  const [baseURL, setBaseURL] = useState(null); // useState to trigger re-renders
  const [debug, setDebug] = useState(false);
  // User data
  const [user, setUser] = useState(null); // Store user data
  const [impersonation, setImpersonation] = useState({ active: false });
  const [userLoading, setUserLoading] = useState(true); // Track loading state
  const [userError, setUserError] = useState(null); // Track any errors
  // Core objects data
  const [jobsList, setJobsList] = useState([]); // Store active job numbers
  const [allJobsList, setAllJobsList] = useState([]); // Store all job numbers (including inactive)
  const [scopesList, setScopesList] = useState([]); // Store scopes of work
  const [announcement, setAnnouncement] = useState(null);

  useMemo(() => {
    // Fetch active jobs (default for most use cases)
    getJobNumbers(true)
      .then(setJobsList)
      .catch((e) => console.error(`could not get active jobs `, e));

    // Fetch all jobs (for admin/management views)
    getJobNumbers(false)
      .then(setAllJobsList)
      .catch((e) => console.error(`could not get all jobs `, e));
  }, []);

  useMemo(() => {
    getScopes()
      .then((incoming) => {
        setScopesList((current) => {
          const next = Array.isArray(incoming) ? incoming : [];
          const ids = new Set(next.map((item) => String(item.id)));
          // Keep a scope created while this request was in flight.
          const extras = (Array.isArray(current) ? current : []).filter(
            (item) => item?.id != null && !ids.has(String(item.id))
          );
          return extras.length === 0
            ? next
            : sortAlphabetically([...next, ...extras], "title");
        });
      })
      .catch((e) => console.error(`could not get scopes `, e));
  }, []);

  useMemo(() => {
    /**
     * Loads backend base URL and DEBUG flag.
     *
     * @returns {Promise<void>}
     */
    async function fetchBaseURL() {
      const response = await getBaseURL();
      setBaseURL(response.baseURL);
      setDebug(Boolean(response.debug));
    }
    fetchBaseURL();
  }, []); // Run only once on page load as this will rarely change (mostly on deploy)

  // Fetch the current user data
  useMemo(() => {
    /**
     * Loads the session user and impersonation metadata.
     *
     * @returns {Promise<void>}
     */
    async function fetchCurrentUser() {
      try {
        setUserLoading(true);
        const response = await getCurrentUser();
        setUser(response.user);
        setImpersonation(response.impersonation || { active: false });
        setUserError(null);
      } catch (error) {
        console.error("Error fetching current user:", error);
        setUserError(error);
      } finally {
        setUserLoading(false);
      }
    }
    fetchCurrentUser();
  }, []); // Run only once on page load

  /**
   * Apply a preferred language to the effective user without toggling loading state.
   *
   * @param {string} language Language code (`en` or `es`).
   * @returns {void}
   */
  const applyPreferredLanguage = useCallback((language) => {
    setUser((current) =>
      current ? { ...current, preferred_language: language } : current
    );
    setImpersonation((current) => {
      if (!current?.active || !current.impersonated_user) {
        return current;
      }
      return {
        ...current,
        impersonated_user: {
          ...current.impersonated_user,
          preferred_language: language,
        },
      };
    });
  }, []);

  /**
   * Optimistically update preferred language, persist it, and revert on failure.
   *
   * @param {string} language Language code (`en` or `es`).
   * @returns {Promise<string>} The saved language code.
   */
  const updatePreferredLanguage = useCallback(
    async (language) => {
      const previous = user?.preferred_language || LANGUAGE_CONFIG.DEFAULT;
      if (!user || language === previous) {
        return previous;
      }

      applyPreferredLanguage(language);
      try {
        await persistPreferredLanguage(language);
        return language;
      } catch (error) {
        applyPreferredLanguage(previous);
        throw error;
      }
    },
    [applyPreferredLanguage, user]
  );

  /**
   * Load the latest unseen announcement. A later null response does not hide
   * a notice already shown in this session.
   *
   * @returns {Promise<void>}
   */
  const fetchAnnouncement = useCallback(async () => {
    try {
      const response = await getLatestAnnouncement();
      if (response?.announcement) {
        setAnnouncement(response.announcement);
      }
    } catch (error) {
      console.error("Error fetching announcement:", error);
    }
  }, []);

  /**
   * Close the announcement modal. The user is already marked seen on the server.
   *
   * @returns {void}
   */
  const clearAnnouncement = useCallback(() => {
    setAnnouncement(null);
  }, []);

  /**
   * Add a catalog work scope created elsewhere in this session.
   *
   * Clock-in and other screens read `scopesList`, which is otherwise loaded once.
   *
   * @param {Object} scope Created WorkScope row (`id` and `title`).
   * @returns {void}
   */
  const addScope = useCallback((scope) => {
    if (scope?.id == null) {
      return;
    }
    invalidateScopesCache();
    setScopesList((current) => upsertAlphabetically(current, scope, "title"));
  }, []);

  /**
   * Keep dropdown catalogs aligned with an office-page edit.
   *
   * Active scopes are inserted or updated. Inactive scopes are removed so
   * clock-in and other screens stop offering them. The cached catalog request
   * is dropped either way.
   *
   * @param {Object} scope Work scope row with `id`, `title`, and `active`.
   * @returns {void}
   */
  const syncCatalogScope = useCallback((scope) => {
    if (scope?.id == null) {
      return;
    }
    invalidateScopesCache();
    setScopesList((current) => {
      const list = Array.isArray(current) ? current : [];
      if (scope.active === false) {
        return list.filter((row) => String(row.id) !== String(scope.id));
      }
      return upsertAlphabetically(list, scope, "title");
    });
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      setUserLoading(true);
      const response = await getCurrentUser({ force: true });
      setUser(response.user);
      setImpersonation(response.impersonation || { active: false });
      setUserError(null);
      return response.user;
    } catch (error) {
      console.error("Error refreshing user data:", error);
      setUserError(error);
      throw error;
    } finally {
      setUserLoading(false);
    }
  }, []);

  const contextValue = useMemo(
    // Memo so if another component calls it doesn't re-run context unless changed
    () => ({
      appName,
      appDomain,
      baseURL,
      debug,
      user,
      impersonation,
      userLoading,
      userError,
      refreshUser,
      updatePreferredLanguage,
      announcement,
      fetchAnnouncement,
      clearAnnouncement,
      jobsList,
      allJobsList,
      scopesList,
      addScope,
      syncCatalogScope,
    }),
    [
      appName,
      appDomain,
      baseURL,
      debug,
      user,
      impersonation,
      userLoading,
      userError,
      refreshUser,
      updatePreferredLanguage,
      announcement,
      fetchAnnouncement,
      clearAnnouncement,
      jobsList,
      allJobsList,
      scopesList,
      addScope,
      syncCatalogScope,
    ]
  );

  return {
    ...contextValue,
  };
}

export default useCoreCtx; // Export the hook as default for backward compatibility
